import { useEffect, useState } from 'react';

import { cn } from '@/utils/cn';

import type { MultipleChoiceElementProps } from '../MultipleChoice.types';

interface RadioProps {
  isChecked: boolean;
  value: string;
  name: string;
  onChange: (value: string) => void;
  className?: string;
  id?: string;
}

const Radio = ({ isChecked, name, value, onChange, className, id }: RadioProps) => {
  return (
    <div className="relative">
      <input
        type="radio"
        name={name}
        value={value}
        id={id}
        onChange={(e) => onChange(e.currentTarget.value)}
        checked={isChecked}
        className="sr-only"
      />
      <div
        className={cn(
          'w-4 h-4 rounded-full border-2 cursor-pointer transition-colors duration-200 flex items-center justify-center',
          isChecked ? 'border-2' : 'bg-white border-gray-300 hover:border-gray-400',
          className,
        )}
        style={
          isChecked
            ? {
                backgroundColor: 'var(--pt-text-on-background-color)',
                borderColor: 'var(--pt-text-on-background-color)',
              }
            : {}
        }
        onClick={() => onChange(value)}
        // biome-ignore lint/a11y/useSemanticElements: We need to use a custom div for styling
        role="radio"
        aria-checked={isChecked}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onChange(value);
          }
        }}
      >
        {isChecked && (
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--pt-background-color)' }} />
        )}
      </div>
    </div>
  );
};

export function MultipleChoiceRadio(props: MultipleChoiceElementProps) {
  const { element, onChange } = props;
  const { name, value, options, description, required, custom_field_id } = element.attrs?.data || {};

  // Internal state for optimistic UI feedback
  const initialValue = Array.isArray(value) ? value[0] || '' : value || '';
  const [internalValue, setInternalValue] = useState(initialValue);

  // Keep internal state in sync if value prop changes
  useEffect(() => {
    const newValue = Array.isArray(value) ? value[0] || '' : value || '';
    setInternalValue(newValue);
  }, [value]);

  if (!name || !custom_field_id || !options) {
    return null;
  }

  const isMultipleChoice = element.type === 'multiple-choice';

  const handleRadioChange = (optionValue: string) => {
    if (internalValue !== optionValue) {
      setInternalValue(optionValue);
      onChange(isMultipleChoice ? [optionValue] : optionValue);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <label htmlFor={custom_field_id} className="font-medium text-sm text-gray-900">
          {name}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
        {description && <p className="text-xs text-gray-700 font-normal">{description}</p>}
      </div>
      <div className="flex flex-col gap-4">
        {options.map((option) => {
          const isChecked = internalValue === option.value;
          const radioName = custom_field_id;
          const radioId = `${custom_field_id}-${option.id}`;
          return (
            <div key={option.id} className="flex flex-row gap-2 items-center">
              <Radio
                isChecked={isChecked}
                name={radioName}
                value={option.value}
                id={radioId}
                onChange={() => handleRadioChange(option.value)}
                className="w-4 h-4"
              />
              <label
                htmlFor={radioId}
                className={cn(
                  'text-sm cursor-pointer',
                  isChecked ? 'text-gray-900 font-medium' : 'text-gray-700 font-normal',
                )}
                onClick={() => handleRadioChange(option.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleRadioChange(option.value);
                  }
                }}
              >
                {option.value}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
}
